import React, { useState, useEffect } from "react";
import { Modal, Spin, Empty, message } from "antd";
import styles from "./CustomerSelectModal.module.css";
import { getCustomerList, Gender } from "@ifa/utils";
import type { CustomerDTO, } from "@ifa/utils";
import CustomButton from "./CustomButton";

interface CustomerSelectModalProps {
  visible: boolean;
  onCancel: () => void;
  onSelect: (customer: CustomerDTO) => void;
}

export const CustomerSelectModal: React.FC<CustomerSelectModalProps> = ({ visible, onCancel, onSelect }) => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<CustomerDTO[]>([]); 
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);    

  const fetchCustomers = async (page: number) => {
    if (loading) return;
    setLoading(true);
    try {
      // @ts-expect-error - The API return type definition mismatches actual response
      const res = await getCustomerList({
        pageNum: page,
        pageSize: 20,
      });

      let list = (res.content || []) as unknown as CustomerDTO[];

      // Compatibility mapping for API returning policyholder objects
      if (list.length > 0 && (list[0] as any).policyholderId) {
        list = list.map((item: any) => ({
          ...item,
          id: item.policyholderId || item.id,
          chineseName: item.policyholderName || item.chineseName,
        }));
      }

      const totalCount = res.page?.totalElements ?? res.totalElements ?? res.total ?? 0;

      if (page === 1) {
        setCustomers(list);
      } else {
        setCustomers((prev) => [...prev, ...list]);
      }
      setTotal(totalCount);
      setPageNum(page);
    } catch (error) {
      console.error("Failed to fetch customers", error);
      // message.error("獲取客戶列表失敗");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setPageNum(1);
      setCustomers([]);
      setTotal(0);
      fetchCustomers(1);
    }
  }, [visible]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Threshold 50px
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (!loading && customers.length < total) {
        fetchCustomers(pageNum + 1);
      }
    }
  };

  const maskIdNumber = (id?: string) => {
    if (!id || id.length < 8) return id;
    const start = id.substring(0, 3);
    const end = id.substring(id.length - 4);
    return `${start}****${end}`;
  };

  const getGenderText = (gender?: string | Gender) => {
    if (!gender) return "--";
    if (gender === Gender.Male || gender === "MALE" || gender === "M") return "男";
    if (gender === Gender.Female || gender === "FEMALE" || gender === "F") return "女";
    return "未知";
  };

  const getDisplayName = (customer: CustomerDTO) => {
    const zhName = customer.chineseName;
    const enName = customer.englishName;

    if (zhName && enName) return `${zhName} (${enName})`;
    if (zhName) return zhName;
    return enName || "未命名客戶";
  };

  const getAge = (customer: CustomerDTO) => {
    if (customer.age !== null && customer.age !== undefined) return customer.age;
    if (customer.birthDate) {
      const birth = new Date(customer.birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age;
    }
    return "--";
  };

  return (
    <Modal title="選擇投保人" open={visible} onCancel={onCancel} footer={null} width={600} className="customer-select-modal" centered>
      <div className={styles["customer-list"]} onScroll={handleScroll}>
        {loading && customers.length === 0 ? (
          <div className={styles["loading-container"]}>
            <Spin size="large" />
          </div>
        ) : customers.length > 0 ? (
          customers.map((customer) => (
            <div key={customer.id} className={styles["customer-card"]}>
              <div className={styles["customer-info"]}>
                <div className={styles["customer-name-row"]}>
                  <span className={styles["customer-name"]}>{getDisplayName(customer)}</span>
                </div>
                <div className={styles["customer-details"]}>
                  <span className={styles["detail-item"]}>身份證號碼: {maskIdNumber(customer.idNumber)}</span>
                  <span className={styles["detail-item"]}>{getAge(customer)}歲</span>
                  {/* Gender might not be in the list response, hide if missing or show unknown */}
                  {customer.gender && <span className={styles["detail-item"]}>{getGenderText(customer.gender)}</span>}
                </div>
              </div>
              <CustomButton
                className={styles["select-btn"]}
                bgColor="#00C7B6"
                onClick={() => onSelect(customer)}
              >
                選擇
              </CustomButton>
            </div>
          ))
        ) : (
          <div className={styles["empty-state"]}>
            <Empty description="暫無客戶資料" />
          </div>
        )}
        {loading && customers.length > 0 && (
          <div style={{ textAlign: "center", padding: "10px" }}>
            <Spin />
          </div>
        )}
      </div>
    </Modal>
  );
};
